import React, { useState } from "react";
import { useMode } from "../lib/mode";
import VisualPanel from "./VisualPanel";
import LockedMapTeaser from "./LockedMapTeaser";
import { addReflection } from "../lib/api";
import { Printer, RotateCcw, Save, ArrowRight, BookOpen, MessageSquareQuote } from "lucide-react";
import { toast } from "sonner";

const SECTIONS = [
  { key: "decision_now",     title: "Your decision now",                    icon: "·" },
  { key: "system_view",      title: "What the system appears to be doing",  icon: "·" },
  { key: "power_view",       title: "Where power currently sits",           icon: "·" },
  { key: "bias_view",        title: "What may be biasing your view",        icon: "·" },
  { key: "safest_next_move", title: "Safest meaningful next move",          icon: "·" },
  { key: "review_trigger",   title: "Review trigger",                       icon: "·" },
];

export default function DecisionBrief({ payload, brief, saved, onRestart }) {
  const { mode, config, memberToken } = useMode();
  const [reflection, setReflection] = useState(saved?.outcome_reflection || "");
  const [savingReflection, setSavingReflection] = useState(false);

  const exportPdf = () => {
    window.print();
  };

  const saveReflection = async () => {
    if (!saved?.id || !memberToken) {
      toast.error("Save the decision first.");
      return;
    }
    setSavingReflection(true);
    try {
      await addReflection(saved.id, { outcome_reflection: reflection, member_token: memberToken });
      toast.success("Reflection added to your map.");
    } catch (e) {
      toast.error("Couldn't save reflection.");
    } finally {
      setSavingReflection(false);
    }
  };

  return (
    <div className="space-y-12" data-testid="decision-brief">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4 pisa-no-print">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#f4b847] mb-2">PISA Decision Brief</p>
          <h1 className="text-3xl md:text-5xl tracking-tight font-medium text-[#1A1A1A]">A private memo, just for you.</h1>
          <p className="text-[#666666] mt-3 max-w-2xl">
            Scenario: <span className="text-[#1A1A1A]">{payload.scenario_type}</span>
            {payload.team_code ? <> · Team: <span className="text-[#1A1A1A]">{payload.team_code}</span></> : null}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exportPdf} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white border border-[#E5E5E5] text-[#1A1A1A] hover:bg-[#F9F9F7] transition-colors text-sm" data-testid="export-pdf-btn">
            <Printer className="h-4 w-4" /> Export PDF
          </button>
          <button onClick={onRestart} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white border border-[#E5E5E5] text-[#1A1A1A] hover:bg-[#F9F9F7] transition-colors text-sm" data-testid="restart-btn">
            <RotateCcw className="h-4 w-4" /> New decision
          </button>
        </div>
      </div>

      {/* Decision prompt as quote */}
      <div className="pisa-card p-7 md:p-10 pisa-enter">
        <div className="flex items-start gap-4">
          <MessageSquareQuote className="h-5 w-5 text-[#2596be] mt-1 shrink-0" />
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#666666] mb-2">Decision in your words</p>
            <p className="text-xl md:text-2xl text-[#1A1A1A] leading-relaxed font-medium">{payload.decision_prompt}</p>
          </div>
        </div>
      </div>

      {/* The brief — typography-led memo */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
        <article className="lg:col-span-3 space-y-10 pisa-enter-stagger" data-testid="brief-memo">
          {SECTIONS.map((s) => (
            <section key={s.key} data-testid={`brief-section-${s.key}`}>
              <div className="flex items-baseline gap-3 mb-3">
                <span className="text-[#f4b847] text-xl leading-none">{s.icon}</span>
                <h3 className="text-xl md:text-2xl tracking-tight font-medium text-[#1A1A1A]">{s.title}</h3>
              </div>
              <p className="text-base md:text-lg text-[#1A1A1A] leading-relaxed pl-6">
                {brief[s.key]}
              </p>
            </section>
          ))}
        </article>

        <aside className="lg:col-span-2 space-y-6">
          <VisualPanel visual={brief.visual} />

          {mode === "member" && saved && (
            <div className="pisa-card p-7 pisa-no-print" data-testid="outcome-reflection-card">
              <p className="text-xs uppercase tracking-[0.2em] text-[#666666] mb-3">Outcome reflection</p>
              <p className="text-sm text-[#666666] mb-4">When you act on this, come back and add what actually happened. The map learns from it.</p>
              <textarea
                rows={4}
                value={reflection}
                onChange={(e) => setReflection(e.target.value)}
                placeholder="What happened? What did you miss?"
                className="w-full bg-[#F9F9F7] border border-[#E5E5E5] rounded-xl p-4 text-sm text-[#1A1A1A] focus:outline-none focus:ring-2 focus:ring-[#2596be]/20 focus:border-[#2596be] transition-all resize-none"
                data-testid="reflection-input"
              />
              <button
                onClick={saveReflection}
                disabled={savingReflection || !reflection.trim()}
                className="mt-3 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1A1A1A] text-white text-sm font-medium hover:bg-black transition-colors disabled:opacity-60"
                data-testid="reflection-save-btn"
              >
                <Save className="h-4 w-4" /> Save reflection
              </button>
            </div>
          )}
        </aside>
      </div>

      {/* Mode-specific CTAs */}
      <div className="pisa-no-print">
        {mode === "public" && (
          <div id="join" className="space-y-6">
            <LockedMapTeaser decisionId={saved?.id} />
          </div>
        )}

        {mode === "mba" && (
          <div className="pisa-card p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center gap-6 md:justify-between" data-testid="mba-cta-block">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#666666] mb-2">Continue your simulation</p>
              <h3 className="text-2xl tracking-tight font-medium text-[#1A1A1A]">Your analysis is private. Take it back into the room.</h3>
            </div>
            <div className="flex flex-wrap gap-3">
              <button onClick={onRestart} className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#2596be] text-white font-medium hover:bg-[#1E7D9F] transition-colors" data-testid="mba-primary-cta">
                {config.primaryCta} <ArrowRight className="h-4 w-4" />
              </button>
              <a href="#" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white border border-[#E5E5E5] text-[#1A1A1A] hover:bg-[#F9F9F7] transition-colors" data-testid="mba-secondary-cta">
                {config.secondaryCta}
              </a>
            </div>
          </div>
        )}

        {mode === "member" && (
          <div className="pisa-card p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center gap-6 md:justify-between" data-testid="member-cta-block">
            <div className="flex items-start gap-4">
              <BookOpen className="h-6 w-6 text-[#2596be] mt-1" />
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-[#666666] mb-2">Already saved</p>
                <h3 className="text-2xl tracking-tight font-medium text-[#1A1A1A]">This decision is now part of your map.</h3>
                <p className="text-[#666666] mt-2 max-w-xl">Scroll down to see how it connects with the others.</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <a href="#map" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#2596be] text-white font-medium hover:bg-[#1E7D9F] transition-colors" data-testid="member-primary-cta">
                {config.primaryCta} <ArrowRight className="h-4 w-4" />
              </a>
              <a href="#" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white border border-[#E5E5E5] text-[#1A1A1A] hover:bg-[#F9F9F7] transition-colors" data-testid="member-secondary-cta">
                {config.secondaryCta}
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
